import React, { useCallback, useEffect, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { MmkItem } from "../../types/mmk";
import { useHorizontalSwipe } from "../../hooks/useHorizontalSwipe";
import { MmkCard } from "./mmk-card";
import { MmkEmptyState } from "./mmk-empty-state";
import { mmkGridStyles } from "./styles";

interface MmkCardsGridProps {
  title: string;
  items: MmkItem[];
  sectionKey?: "pinned" | "to_review" | "known";
  viewMode: "grid" | "scroll";
  activeCardId: string | null;
  flippedCardId: string | null;
  isSelectionMode: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  emptyTitle: string;
  emptySubtitle?: string;
  onFlip: (id: string) => void;
  onCloseFlip: () => void;
  onPin: (id: string) => void;
  onReviewLater: (id: string) => void;
  onKnowIt: (id: string) => void;
  onEditPersonal?: (id: string) => void;
  onDeletePersonal?: (id: string) => void;
}

export const MmkCardsGrid: React.FC<MmkCardsGridProps> = ({
  title,
  items,
  sectionKey,
  viewMode,
  activeCardId,
  flippedCardId,
  isSelectionMode,
  selectedIds,
  onToggleSelect,
  emptyTitle,
  emptySubtitle,
  onFlip,
  onCloseFlip,
  onPin,
  onReviewLater,
  onKnowIt,
  onEditPersonal,
  onDeletePersonal,
}) => {
  const [scrollIndex, setScrollIndex] = useState(0);
  const isScroll = viewMode === "scroll";

  useEffect(() => {
    setScrollIndex((prev) => Math.min(prev, Math.max(0, items.length - 1)));
  }, [items.length]);

  const goPrev = useCallback(() => {
    setScrollIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const goNext = useCallback(() => {
    setScrollIndex((prev) => Math.min(items.length - 1, prev + 1));
  }, [items.length]);

  const swipeHandlers = useHorizontalSwipe({
    enabled: isScroll && items.length > 1,
    onSwipeLeft: goNext,
    onSwipeRight: goPrev,
  });

  useEffect(() => {
    if (!isScroll) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        goPrev();
      }
      if (event.key === "ArrowRight") {
        goNext();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isScroll, goPrev, goNext]);

  const visibleItems = isScroll
    ? items.slice(scrollIndex, scrollIndex + 1)
    : items;
  const visibleItemId = visibleItems[0]?.id;

  useEffect(() => {
    if (!isScroll) return;
    if (!activeCardId && !flippedCardId) return;

    const hasActiveInThisSection =
      !!activeCardId && items.some((i) => i.id === activeCardId);
    const hasFlippedInThisSection =
      !!flippedCardId && items.some((i) => i.id === flippedCardId);
    if (!hasActiveInThisSection && !hasFlippedInThisSection) return;

    const activeOutOfView = !!activeCardId && visibleItemId !== activeCardId;
    const flippedOutOfView = !!flippedCardId && visibleItemId !== flippedCardId;
    if (visibleItemId && (activeOutOfView || flippedOutOfView)) {
      onCloseFlip();
    }
  }, [
    isScroll,
    flippedCardId,
    activeCardId,
    visibleItemId,
    onCloseFlip,
    items,
  ]);

  return (
    <Box sx={{ mb: 3, position: "relative" }}>
      <Typography
        variant="subtitle1"
        sx={(theme) => ({
          fontWeight: 700,
          mb: 1.5,
          color:
            theme.palette.mode === "dark"
              ? "#FFFFFF"
              : theme.palette.text.primary,
        })}
      >
        {title}
      </Typography>
      <Box
        {...(isScroll ? swipeHandlers : {})}
        sx={
          isScroll
            ? {
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
                touchAction: "pan-y",
              }
            : mmkGridStyles
        }
      >
        {items.length === 0 ? (
          <MmkEmptyState title={emptyTitle} subtitle={emptySubtitle} />
        ) : (
          visibleItems.map((item) => (
            <motion.div
              key={item.id}
              layout
              layoutId={item.id}
              data-mmk-card-id={item.id}
              transition={{
                layout: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
              }}
              style={{
                position: "relative",
                zIndex: activeCardId === item.id ? 1301 : 1,
              }}
            >
              <MmkCard
                item={item}
                isActive={activeCardId === item.id}
                isFlipped={flippedCardId === item.id}
                isSelectionMode={isSelectionMode}
                isSelected={selectedIds.has(item.id)}
                sectionKey={sectionKey}
                fullLength={isScroll}
                onToggleSelect={() => onToggleSelect(item.id)}
                onFlip={() => onFlip(item.id)}
                onClose={onCloseFlip}
                onPin={() => onPin(item.id)}
                onReviewLater={() => onReviewLater(item.id)}
                onKnowIt={() => onKnowIt(item.id)}
                onEditPersonal={
                  onEditPersonal ? () => onEditPersonal(item.id) : undefined
                }
                onDeletePersonal={
                  onDeletePersonal
                    ? () => onDeletePersonal(item.id)
                    : undefined
                }
              />
            </motion.div>
          ))
        )}
      </Box>
      {isScroll && items.length > 1 && (
        <Box
          sx={{
            mt: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            width: "100%",
          }}
        >
          <IconButton
            size="small"
            disabled={scrollIndex === 0}
            onClick={goPrev}
          >
            <ChevronLeft size={16} />
          </IconButton>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {scrollIndex + 1} / {items.length}
          </Typography>
          <IconButton
            size="small"
            disabled={scrollIndex >= items.length - 1}
            onClick={goNext}
          >
            <ChevronRight size={16} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};
